import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { useTranslation } from 'react-i18next';
import { useState, useCallback } from 'react';
import { Accordion } from '@reach/accordion';
import { IconButton, Text, tokens, utils } from '@hotjar/hotsauce';
import type { DateRange, GlobalFilter } from '@hotjar/global-filters';
import { useFeatureFlag } from '@hotjar/feature-flags';
import '@reach/accordion/styles.css';

import { AccordionSection } from './PanelAccordion/AccordionSection';
import { useManualRetaker } from '../../hooks/useManualRetaker';
import { ManualRetakerUrlModal } from '../ManualRetakerUrlModal/ManualRetakerUrlModal';
import type { ChangeScreenshotHandler } from '../../constants-types/ChangeScreenshotTypes';
import {
  SMART_PANEL_SECONDARY_VIEWS,
  type TEngagementSecondaryView,
  type TSupportSecondaryView,
} from '../../constants-types/SmartPanelSecondaryViews';
import {
  CONTENT_RIGHT_LEFT_PADDING,
  SMART_PANEL_COLLAPSED_WIDTH,
  SMART_PANEL_EXPANDED_WIDTH,
  STATUSBAR_HEIGHT,
  SMART_PANEL_SPACING,
} from '../../constants-types/Layout';
import type { TrackableSite, HighlightsProps } from '../../constants-types/ExternalDependencyTypes';
import { SMART_PANEL_LAYERS_SECTION } from '../../constants-types/HeatmapFeatureFlags';
import { isDurationLongerThan3Months } from '../../lib/heatmapDateRangeConverter';
import { smartPanelAccordionBorderStyles } from '../../utils/layout';
import {
  SmartPanelSection,
  SmartPanelSecondaryView,
  SmartPanelSegmentColumn,
} from './SmartPanelBlocks';
import { TotalClicksSegment } from './TotalClicksSegment';
import { RageClicksAndUTurnsSegments } from './RageClicksAndUTurnsSegments';
import { DropOffRateSegment } from './DropOffRateSegment';
import { AverageVisitDurationSegment } from './AverageVisitDurationSegment';
import { SessionErrorsSegment } from './SessionErrorsSegment/SessionErrorsSegment';
import { ScreenshotSegment } from './ScreenshotSegment';
import { SupportSection } from './SupportSection';
import { SupportSecondaryViews } from './SupportSecondaryViews/SupportSecondaryViews';
import { FeedbackSection } from './FeedbackSection';
import { LayersSection } from './LayersSection';
import { EngagementSecondaryView } from './EngagementSecondaryView/EngagementSecondaryView';

interface SmartPanelProps {
  isOpen: boolean;
  isHeatLayerActive: boolean;
  isTopClicksWidgetActive: boolean;
  toggle: () => void;
  clickCount?: number;
  currentSite?: TrackableSite;
  onHeatLayerToggle: (isActive: boolean) => void;
  onRetakeScreenshotClicked: ChangeScreenshotHandler;
  onTopClicksWidgetToggle: (isActive: boolean) => void;
  originUrl: string | null;
  screenshotTimestamp: number | null;
  selectedDeviceType: HeatmapDeviceTypes;
  selectedHeatType: HeatmapDataType;
  selectedDateRange: DateRange;
  selectedTargetingValue: TargetingValue;
  searchPattern: string;
  selectedFiltersInUiFormat: GlobalFilter;
  isErrorReportingFFEnabled: boolean;
  planAllowsErrorCapturing: boolean;
  highlights?: HighlightsProps;
  isEngagementZonesActive: boolean;
  onEngagementZonesToggle: (isActive: boolean) => void;
  isEngagementZonesAvailable: boolean;
  isFetchingEngagementData: boolean;
}

const Container = styled.aside<{ isOpen: boolean }>`
  display: flex;
  flex-direction: column;
  position: absolute;
  min-height: calc(100% - ${STATUSBAR_HEIGHT}px);
  top: ${STATUSBAR_HEIGHT}px;
  width: ${({ isOpen }) => (isOpen ? SMART_PANEL_EXPANDED_WIDTH : SMART_PANEL_COLLAPSED_WIDTH)}px;
  right: -${CONTENT_RIGHT_LEFT_PADDING};
  background-color: ${tokens.color.ui_background};
  border-left: 1px solid ${tokens.color.ui_border_light};
  border-right: 0;
  border-top: 0;
  z-index: ${utils.ZINDEX.MODAL - 1};
  border-bottom: 1px solid ${tokens.color.ui_border_light};
`;

const Header = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${tokens.spacer.x1} ${tokens.spacer.x2} ${tokens.spacer.x1} ${SMART_PANEL_SPACING};
  border-bottom: 1px solid ${tokens.color.ui_border_light};
`;

const ExpandButton = styled(IconButton)`
  margin: ${SMART_PANEL_SPACING} auto;
`;

const SectionsWrapper = styled.div<{ isPanelOpen: boolean }>`
  display: ${({ isPanelOpen }) => (isPanelOpen ? 'initial' : 'none')};
`;

export const SmartPanel = ({
  isOpen,
  isHeatLayerActive,
  isTopClicksWidgetActive,
  toggle,
  clickCount,
  currentSite,
  onHeatLayerToggle,
  onRetakeScreenshotClicked,
  onTopClicksWidgetToggle,
  originUrl,
  screenshotTimestamp,
  selectedDeviceType,
  selectedHeatType,
  selectedDateRange,
  selectedTargetingValue,
  searchPattern,
  selectedFiltersInUiFormat,
  isErrorReportingFFEnabled,
  planAllowsErrorCapturing,
  highlights,
  isEngagementZonesActive,
  onEngagementZonesToggle,
  isEngagementZonesAvailable,
  isFetchingEngagementData,
}: SmartPanelProps): JSX.Element => {
  const { t } = useTranslation('heatmaps');
  const [isSecondaryViewActive, setIsSecondaryViewActive] = useState<boolean>(false);
  const [activeSecondaryView, setActiveSecondaryView] = useState<
    TSupportSecondaryView | TEngagementSecondaryView | undefined
  >(undefined);

  const isEngagementSecondaryView =
    activeSecondaryView === SMART_PANEL_SECONDARY_VIEWS.HM_ENGAGEMENT_HELP;

  const isSupportSecondaryView =
    activeSecondaryView === SMART_PANEL_SECONDARY_VIEWS.HM_DOES_NOT_LOOK_RIGHT ||
    activeSecondaryView === SMART_PANEL_SECONDARY_VIEWS.HM_NOT_CAPTURING_URL ||
    activeSecondaryView === SMART_PANEL_SECONDARY_VIEWS.HM_SHOWS_COOKIES_BANNER;

  const onManualRetakerSuccess = useCallback(
    (html: string, sourceTrigger: RetakerTriggerSource) => {
      onRetakeScreenshotClicked({ html }, true, sourceTrigger);
    },
    [onRetakeScreenshotClicked],
  );

  const {
    handleManualRetakerClick,
    handleManualRetakerUrlModalClose,
    handleManualRetakerUrlSubmit,
    isManualRetakerUrlModalOpen,
  } = useManualRetaker(selectedDeviceType, onManualRetakerSuccess);

  const shouldDisablePageInsightsAndEngagementZones =
    isDurationLongerThan3Months(selectedDateRange);
  const disabledTooltipText = t('smart-panel.disabled-insight');

  const isLayersSectionEnabled = useFeatureFlag(SMART_PANEL_LAYERS_SECTION);

  const handleEngagementInfoIconClick = () => {
    setIsSecondaryViewActive(true);
    setActiveSecondaryView(SMART_PANEL_SECONDARY_VIEWS.HM_ENGAGEMENT_HELP);
  };

  return (
    <Container isOpen={isOpen}>
      {!isSecondaryViewActive &&
        (isOpen ? (
          <Header>
            <Text textType="subtitle2">{t('smart-panel.heading')}</Text>
            <IconButton
              kind="ghost"
              icon="close"
              size="s"
              title={t('smart-panel.collapse')}
              tooltipText={t('smart-panel.collapse')}
              description={t('smart-panel.collapse')}
              onClick={toggle}
            />
          </Header>
        ) : (
          <ExpandButton
            kind="ghost"
            icon="expand_left"
            size="s"
            title={t('smart-panel.expand')}
            tooltipText={t('smart-panel.expand')}
            description={t('smart-panel.expand')}
            onClick={toggle}
          />
        ))}
      <SectionsWrapper isPanelOpen={isOpen}>
        {!isSecondaryViewActive && (
          <Accordion defaultIndex={[0, 1, 2, 3]} multiple>
            <AccordionSection
              headerCustomCss={css`
                padding: 0;
                ${smartPanelAccordionBorderStyles()}
              `}
              title={<Text textType="subtitle2">{t('smart-panel.screenshot-details')}</Text>}
            >
              <SmartPanelSection>
                <ScreenshotSegment
                  currentSite={currentSite}
                  onRefreshScreenshotClicked={onRetakeScreenshotClicked}
                  screenshotTimestamp={screenshotTimestamp}
                  selectedDateRange={selectedDateRange}
                  handleManualRetakerClick={handleManualRetakerClick}
                  originUrl={originUrl}
                  selectedTargetingValue={selectedTargetingValue}
                />
              </SmartPanelSection>
            </AccordionSection>
            {isLayersSectionEnabled && (
              <AccordionSection
                headerCustomCss={css`
                  padding: 0;
                  ${smartPanelAccordionBorderStyles()}
                `}
                title={<Text textType="subtitle2">{t('smart-panel.overlays')}</Text>}
              >
                <LayersSection
                  isHeatLayerActive={isHeatLayerActive}
                  onHeatLayerToggle={onHeatLayerToggle}
                  isTopClicksWidgetActive={isTopClicksWidgetActive}
                  onTopClicksWidgetToggle={onTopClicksWidgetToggle}
                  heatType={selectedHeatType}
                  device={selectedDeviceType}
                  highlights={highlights}
                  currentSite={currentSite}
                  isEngagementZonesActive={isEngagementZonesActive}
                  onEngagementZonesToggle={onEngagementZonesToggle}
                  onEngagementInfoIconClick={handleEngagementInfoIconClick}
                  isEngagementZonesAvailable={isEngagementZonesAvailable}
                  isFetchingEngagementData={isFetchingEngagementData}
                  isEngagementZonesDisabled={shouldDisablePageInsightsAndEngagementZones}
                  engagementZonesDisabledTooltipText={disabledTooltipText}
                />
              </AccordionSection>
            )}
            <AccordionSection
              headerCustomCss={css`
                padding: 0;
                ${smartPanelAccordionBorderStyles()}
              `}
              title={<Text textType="subtitle2">{t('smart-panel.quick-stats')}</Text>}
            >
              <SmartPanelSection>
                <TotalClicksSegment
                  currentSite={currentSite}
                  device={selectedDeviceType}
                  value={clickCount}
                  heatType={selectedHeatType}
                  isTopClicksWidgetActive={isTopClicksWidgetActive}
                  onTopClicksWidgetToggle={onTopClicksWidgetToggle}
                  shouldHideTopClicksWidget={isLayersSectionEnabled}
                />
                <RageClicksAndUTurnsSegments
                  selectedDateRange={selectedDateRange}
                  selectedTargetingValue={selectedTargetingValue}
                  selectedDeviceType={selectedDeviceType}
                  searchPattern={searchPattern}
                  isDisabled={shouldDisablePageInsightsAndEngagementZones}
                  disabledTooltipText={disabledTooltipText}
                />
                <DropOffRateSegment
                  selectedDateRange={selectedDateRange}
                  selectedTargetingValue={selectedTargetingValue}
                  selectedDeviceType={selectedDeviceType}
                  searchPattern={searchPattern}
                  currentSite={currentSite}
                  isDisabled={shouldDisablePageInsightsAndEngagementZones}
                  disabledTooltipText={disabledTooltipText}
                />
                <AverageVisitDurationSegment
                  selectedDateRange={selectedDateRange}
                  selectedTargetingValue={selectedTargetingValue}
                  selectedDeviceType={selectedDeviceType}
                  searchPattern={searchPattern}
                  currentSite={currentSite}
                  isDisabled={shouldDisablePageInsightsAndEngagementZones}
                  disabledTooltipText={disabledTooltipText}
                />
                {isErrorReportingFFEnabled && (
                  <SessionErrorsSegment
                    selectedDateRange={selectedDateRange}
                    selectedTargetingValue={selectedTargetingValue}
                    selectedDeviceType={selectedDeviceType}
                    searchPattern={searchPattern}
                    currentSite={currentSite}
                    selectedFiltersInUiFormat={selectedFiltersInUiFormat}
                    planAllowsErrorCapturing={planAllowsErrorCapturing}
                  />
                )}
              </SmartPanelSection>
            </AccordionSection>
            <FeedbackSection
              currentSite={currentSite}
              pageUrl={searchPattern}
              selectedTargetingValue={selectedTargetingValue}
            />
            <AccordionSection
              headerCustomCss={css`
                padding: 0;
              `}
              title={<Text textType="subtitle2">{t('smart-panel.common-questions')}</Text>}
            >
              <SupportSection
                currentSite={currentSite}
                onActivateSecondarySection={(activeView: TSupportSecondaryView) => {
                  setIsSecondaryViewActive(true);
                  setActiveSecondaryView(activeView);
                }}
              />
            </AccordionSection>
          </Accordion>
        )}

        {isSecondaryViewActive && (
          <SmartPanelSecondaryView
            onDeActivateSecondaryView={() => {
              setIsSecondaryViewActive(false);
              setActiveSecondaryView(undefined);
            }}
          >
            {isSupportSecondaryView && (
              <SmartPanelSegmentColumn>
                <SupportSecondaryViews
                  activeView={activeSecondaryView}
                  currentSite={currentSite}
                  handleManualRetakerClick={handleManualRetakerClick}
                  originUrl={originUrl}
                  selectedTargetingValue={selectedTargetingValue}
                />
              </SmartPanelSegmentColumn>
            )}
            {isEngagementSecondaryView && <EngagementSecondaryView />}
          </SmartPanelSecondaryView>
        )}
      </SectionsWrapper>
      <ManualRetakerUrlModal
        isOpen={isManualRetakerUrlModalOpen}
        onSubmit={handleManualRetakerUrlSubmit}
        onClose={handleManualRetakerUrlModalClose}
      />
    </Container>
  );
};
